import { useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';

import { BusinessOutlined, PersonOutline } from '@mui/icons-material';
import { Avatar, Box, ButtonBase, Popover, SxProps, Theme, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { currentUserApi } from '../../axios';
import { useFeatureFlags, useLanguage, usePermissionFlags } from '../../hooks';
import { LogOutOutline } from '../../icons';
import { useUserDataStore } from '../../newStore/userDataStore';
import { paths, redirectTo } from '../../utils/navigation';
import { isSupportOrAdmin } from '../../utils/utils';
import { MfaModal } from '../MfaModal/MfaModal';
import { AccountSwitcher } from './AccountSwitcher';
import { AccountSwitchTransition } from './AccountSwitchTransition';
import { AllAccountsPanel } from './AllAccountsPanel';
import { useAccountSwitch } from './useAccountSwitch';
import { UserActionsOverview } from './UserActionsOverview';

const customStyles = {
  avatarButton: {
    borderRadius: '50%',
  },
  avatar: {
    width: '40px',
    height: '40px',
    backgroundColor: (theme) => theme.palette.primary.main,
    color: '#FFFFFF',
    fontSize: '18px',
    fontWeight: 600,
  },
  popover: {
    marginTop: '8px',
  },
  // The width goes on the paper and not in a descendant selector from the root:
  // MUI's own `minWidth: 16px` beats the cascade from outside.
  //
  // With a width of its own the row stops depending on whatever space is left up
  // to the edge of the screen, which is half of the name truncation. The other
  // half is the right-side `transformOrigin` below.
  popoverPaper: {
    borderRadius: '12px',
    minWidth: '280px',
    maxWidth: '380px',
  },
  popoverContent: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  popoverItem: {
    fontSize: '14px',
    fontWeight: '400',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
    gap: '8px',
    '&:hover': {
      color: (theme) => theme.palette.primary.main,
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  iconSize: {
    fontSize: '16px',
  },
} satisfies Record<string, SxProps<Theme>>;

export function UserNav() {
  const lang = useLanguage();
  const currentUser = useUserDataStore((state) => state.currentUser);
  const [searchParams] = useSearchParams();
  const isFromMobileApp = searchParams.get('redirect_to_frontend') === 'true';
  const pdfMode = searchParams.get('pdf_mode') ? true : false;

  const { logoutUser } = useUserDataStore((state) => state.actions);
  const { enqueueSnackbar } = useSnackbar();
  const { checkFlag } = useFeatureFlags();
  const { checkPermission } = usePermissionFlags();

  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  // Above the popover on purpose: it unmounts the selector on close, and both the
  // single-flight guard and the row spinner have to outlive that. The menu is left
  // open while a switch runs so the spinner stays on screen; the reload closes it.
  const { switchingTo, switchingToName, switchAccount } = useAccountSwitch();
  // The full account list is not held by the selector: the popover unmounts its
  // children on close, and the modal has to stay up over the closed menu. The
  // anchor is kept while it is open so going back reopens the menu where it was.
  const [allAccountsOpen, setAllAccountsOpen] = useState(false);
  const [openMfaModal, setOpenMfaModal] = useState(false);
  const [lastMfaStatus, setLastMfaStatus] = useState<boolean | null>(null);

  // Check MFA status and auto-open modal if needed
  useEffect(() => {
    const checkMfaStatus = async () => {
      if (
        !currentUser?.id ||
        currentUser?.auth_type !== 'cognito' ||
        !checkFlag('MFACognito') ||
        pdfMode
      ) {
        return;
      }

      const justLoggedIn = sessionStorage.getItem(`justLoggedIn-${currentUser?.id}`);
      if (justLoggedIn === 'true') {
        return;
      }

      try {
        const data = await currentUserApi.getMfaSettingsStatus();

        if (data.settings === 'mandatory' || data.settings === 'optional') {
          const mfaStatus = await currentUserApi.getMfaStatus();

          // Reset dismissal state if MFA status changed
          if (lastMfaStatus !== null && lastMfaStatus !== mfaStatus.mfa_enabled) {
            sessionStorage.setItem(`justLoggedIn-${currentUser?.id}`, 'false');
          }
          setLastMfaStatus(mfaStatus.mfa_enabled);

          if (!mfaStatus.mfa_enabled) {
            sessionStorage.setItem(`justLoggedIn-${currentUser?.id}`, 'true');
            setOpenMfaModal(true);
          }
        }
      } catch (error) {
        console.error('Error checking MFA status:', error);
      }
    };

    checkMfaStatus();
  }, []);

  if (!currentUser?.id && !currentUser?.is_support) return <></>;

  const isFreeUser = !currentUser?.is_paying;
  const isAdmin = currentUser?.is_admin;
  const hasFullAccess = isSupportOrAdmin(currentUser);

  const userHasNewBilling = (checkFlag('ActionsOverview') && !isFreeUser) || isFreeUser;
  const userHasPermissionsForActionsOverview = hasFullAccess || isAdmin;
  const actionsOverviewEnabled = userHasNewBilling && userHasPermissionsForActionsOverview;

  const handleDeleteJustLoggedIn = () => {
    if (currentUser?.id && currentUser?.auth_type === 'cognito') {
      try {
        sessionStorage.removeItem(`justLoggedIn-${currentUser.id}`);
      } catch (error) {
        console.error('Failed to remove justLoggedIn item:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      setUserMenuAnchorEl(null);
      handleDeleteJustLoggedIn();
      await currentUserApi.logout();
      logoutUser();
      redirectTo('', true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(lang.login.logoutError, { variant: 'error' });
    }
  };

  return (
    <>
      <AccountSwitchTransition accountName={switchingTo === null ? null : switchingToName} />
      <ButtonBase
        sx={customStyles.avatarButton}
        onClick={(e) => setUserMenuAnchorEl(e.currentTarget)}
        aria-haspopup='menu'
        aria-expanded={!!userMenuAnchorEl}
        aria-label={lang.mainTopBar.myAccount}
        data-testid='user-nav-avatar'
      >
        <Avatar sx={customStyles.avatar}>
          {`${currentUser?.username?.charAt(0)}${currentUser?.last_name?.charAt(0)}`}
        </Avatar>
      </ButtonBase>
      <Popover
        open={!!userMenuAnchorEl}
        onClose={() => setUserMenuAnchorEl(null)}
        anchorEl={userMenuAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        // By the RIGHT edge: the avatar sits against the edge of the screen, and
        // anchoring by the left made the menu grow outwards, MUI shrank it to keep
        // it visible and the names truncated because of the position.
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={customStyles.popover}
        slotProps={{ paper: { sx: customStyles.popoverPaper } }}
      >
        <Box sx={customStyles.popoverContent}>
          <AllAccountsPanel
            open={allAccountsOpen}
            switchingTo={switchingTo}
            onSwitch={switchAccount}
            onBack={() => setAllAccountsOpen(false)}
            onClose={() => {
              setAllAccountsOpen(false);
              setUserMenuAnchorEl(null);
            }}
          />
          {!allAccountsOpen && actionsOverviewEnabled && <UserActionsOverview />}
          {!allAccountsOpen && (
            <AccountSwitcher
              switchingTo={switchingTo}
              onSwitch={switchAccount}
              onSeeAllAccounts={() => setAllAccountsOpen(true)}
            />
          )}
          {!allAccountsOpen && (
            <>
              {(checkPermission('admin_account_config') || hasFullAccess) && (
                <Typography
                  component={NavLink}
                  to={paths.account.index}
                  sx={customStyles.popoverItem}
                  onClick={() => setUserMenuAnchorEl(null)}
                >
                  <BusinessOutlined
                    fontSize='inherit'
                    color='inherit'
                    sx={customStyles.iconSize}
                  />
                  {lang.mainTopBar.companySettings}
                </Typography>
              )}
              <Typography
                component={NavLink}
                to={`${paths.users.index}/${currentUser?.id}`}
                sx={customStyles.popoverItem}
                onClick={() => setUserMenuAnchorEl(null)}
              >
                <PersonOutline
                  fontSize='inherit'
                  color='inherit'
                  sx={customStyles.iconSize}
                />
                {lang.mainTopBar.myAccount}
              </Typography>
              <ButtonBase
                sx={customStyles.popoverItem}
                onClick={handleLogout}
                data-testid='user-nav-logout'
              >
                <LogOutOutline
                  fontSize='inherit'
                  color='inherit'
                  sx={customStyles.iconSize}
                />
                {lang.mainTopBar.logout}
              </ButtonBase>
            </>
          )}
        </Box>
      </Popover>
      <MfaModal
        open={openMfaModal}
        showActivateInSuccess={!isFromMobileApp}
        onClose={() => setOpenMfaModal(false)}
        isMobile={isFromMobileApp}
      />
    </>
  );
}
